import { useRef } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useContentLanguage } from "./content-language-context"
import { getScheduleTranslation } from "./controller/get-schedule-translation"
import { getTranslatedSkills } from "./controller/get-translated-skills"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { useMessageProgramsStatus } from "lib/localization/use-message-programs-status"
import { TranslationStateChanged } from "./translation-event-definitions.mjs"

/**
 * Component that manages translation state and applies translations in-place
 * to the schedule object. Sits inside <Bound> and reads translationState ref
 * from context. When the language changes, only THIS component re-renders
 * (via useContentLanguage); children pass through unchanged because React
 * skips reconciliation on stable children props.
 *
 * This replaces useTranslatedSchedule by:
 * - Mutating the schedule target in-place (same object reference)
 * - Storing translation metadata on a shared ref (translationState)
 * - Raising TranslationStateChanged so only subscribed consumers re-render
 */
export function TranslationStateProvider({ children }) {
    const { language, rtl } = useContentLanguage()
    const { translationState, target: schedule } = useBoundContext()
    const messagePrograms = useMessageProgramsStatus()
    const shouldTranslate = language !== "en" && !!schedule?._id

    const scheduleId = shouldTranslate ? schedule._id : "none"
    const translation = getScheduleTranslation.useResultsOnce({ scheduleId, language }, withInitialValue(IS_LOADING))
    const translatedSkills = getTranslatedSkills.useResultsOnce(
        { language: shouldTranslate ? language : "en" },
        withInitialValue(IS_LOADING)
    )

    const receivedLanguageRef = useRef(null)
    const originalsRef = useRef(null)
    const lastScheduleIdRef = useRef(null)

    // Track received translation language (detects stale cached results).
    // When switching languages, useResultsOnce may return a stale cached
    // result for the previous language. receivedLanguageRef tracks which
    // language the RECEIVED translation belongs to so we can detect staleness.
    const isLoading = translation === IS_LOADING
    const translationError = !isLoading && translation?._meta?.status === "error" ? translation._meta.error : null
    if (!isLoading && shouldTranslate && translation?._meta?.language) {
        receivedLanguageRef.current = translation._meta.language
    } else if (!shouldTranslate) {
        receivedLanguageRef.current = null
    }

    const isWaitingForResult =
        shouldTranslate && !isLoading && !translationError && receivedLanguageRef.current !== language
    const messageProgramsReady =
        !shouldTranslate ||
        language === "en" ||
        messagePrograms.status === "error" ||
        (messagePrograms.status === "ready" && messagePrograms.language === language)
    const loading = shouldTranslate && !translationError && (isLoading || isWaitingForResult || !messageProgramsReady)

    // Reset originals when schedule identity changes
    if (schedule?._id !== lastScheduleIdRef.current) {
        lastScheduleIdRef.current = schedule?._id
        originalsRef.current = null
    }

    // Mutate shared state ref synchronously (idempotent)
    if (translationState) {
        const state = translationState.current
        state.loading = loading
        state.language = language
        state.rtl = loading ? false : rtl
        state.error = translationError
        state.isTranslated = shouldTranslate && !isLoading && !translationError && !!translation && !isWaitingForResult
        state.translatedTitle = state.isTranslated ? translation?.title ?? null : null
        if (!shouldTranslate) {
            state.translationMeta = null
        } else if (!isLoading && !isWaitingForResult) {
            state.translationMeta = translation?._meta ?? null
        }
        if (!shouldTranslate) {
            state.labels = null
        } else if (!isLoading && !isWaitingForResult && translation?.labels) {
            state.labels = translation.labels
        }
        state.translatedSkills = translatedSkills === IS_LOADING ? null : translatedSkills ?? null

        if (schedule) {
            applyTranslation()
        }

        TranslationStateChanged.raiseOnce()
    }

    return children

    function applyTranslation() {
        if (!shouldTranslate) {
            restoreOriginals()
            return
        }

        if (isLoading || !messageProgramsReady || !translation || isWaitingForResult) return

        captureOriginals()
        mergeTranslation()
    }

    function captureOriginals() {
        if (originalsRef.current) return

        const taskOriginals = {}
        if (schedule.tasks) {
            for (const task of schedule.tasks) {
                taskOriginals[task.id] = {
                    title: task.title,
                    content: task.content,
                    note: task.note,
                }
            }
        }
        originalsRef.current = {
            title: schedule.title,
            introContent: schedule.intro?.content,
            introNote: schedule.intro?.note,
            tasks: taskOriginals,
        }
    }

    function mergeTranslation() {
        const originals = originalsRef.current

        schedule.title = translation.title ?? originals.title

        if (translation.intro && schedule.intro) {
            schedule.intro.content = translation.intro.content ?? originals.introContent
            schedule.intro.note = translation.intro.note ?? originals.introNote
        }

        if (translation.tasks && schedule.tasks) {
            for (const task of schedule.tasks) {
                const taskTranslation = translation.tasks[task.id]
                const taskOriginal = originals.tasks[task.id]
                if (taskTranslation && taskOriginal) {
                    task.title = taskTranslation.title ?? taskOriginal.title
                    task.content = taskTranslation.content ?? taskOriginal.content
                    task.note = taskTranslation.note ?? taskOriginal.note
                    task._verified = taskTranslation.verified
                } else if (taskOriginal) {
                    task.title = taskOriginal.title
                    task.content = taskOriginal.content
                    task.note = taskOriginal.note
                    delete task._verified
                }
            }
        }

        schedule._translated = true
        schedule._translationLanguage = translation._meta?.language
        schedule._translationRtl = rtl
        schedule._labels = translation.labels ?? null
        schedule._translatedSkills = translatedSkills === IS_LOADING ? null : translatedSkills ?? null
    }

    function restoreOriginals() {
        if (!originalsRef.current) return

        const originals = originalsRef.current
        schedule.title = originals.title
        if (schedule.intro) {
            schedule.intro.content = originals.introContent
            schedule.intro.note = originals.introNote
        }
        if (schedule.tasks && originals.tasks) {
            for (const task of schedule.tasks) {
                const orig = originals.tasks[task.id]
                if (orig) {
                    task.title = orig.title
                    task.content = orig.content
                    task.note = orig.note
                    delete task._verified
                }
            }
        }
        delete schedule._translated
        delete schedule._translationLanguage
        delete schedule._translationRtl
        delete schedule._labels
        delete schedule._translatedSkills
        originalsRef.current = null
    }
}
